<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType, NianMonsterType, PersonMonsterType } from '@/types/monster'
import type { DailyChallengeModeType, PersonFightModeType } from '@/types/user'
import { socketEmit, socketEmitAsync, socketOn } from '@/utils/web-socket'
import { defineAsyncComponent, ref, watch } from 'vue'
import { computed, reactive } from 'vue'
import DungeonFight from './components/Fight.vue'
import type { EternalTowerInfoRes, GoldTempleInfoRes, NestInfoRes } from '@/types/dungeon'
import type { PersonFightInfoType, TeamFightInfoType } from '@/types/fight'
import { dayjs, ElMessage } from 'element-plus'
import EternalTower from './components/EternalTower.vue'
import Nest from './components/Nest.vue'
import Plain from './components/Plain.vue'
import TeamFight from './components/TeamFight.vue'
import { useRoguelikeGameStore } from '@/stores/modules/miniGames'
import RoguelikePanel from './components/RoguelikePanel.vue'
import { QuestionFilled, ArrowDown, ArrowUp } from '@element-plus/icons-vue'
import RuneTower from './components/RuneTower.vue'
import RuneTemple from './components/RuneTemple.vue'
import RuneRuin from './components/RuneRuin.vue'
import { useRoomStore } from '@/stores/modules/room'
import Crack from './components/Crack.vue'
import Guide from './components/Guide.vue'
import Wild from './components/Wild.vue'
import { useWildStore } from '@/stores/modules/wild'
import AdvanceRoad from './components/AdvanceRoad.vue'
import EndlessTower from './components/EndlessTower.vue'
import GoldTemple from './components/GoldTemple.vue'
import TeamFightRoom from './components/TeamFightRoom.vue'
import type { RoomType } from '@/types/room'
import RelicRuin from './components/RelicRuin.vue'
import SkillCorridor from './components/SkillCorridor.vue'
import { goldTempleRank, drageonMountainRankHpMag } from './data'
import SecretCave from './components/SecretCave.vue'
import DrageonMountain from './components/DrageonMountain.vue'
import PeakRoad from './components/PeakRoad.vue'
import BoundlessTower from './components/BoundlessTower.vue'
import NianMonster from './components/NianMonster.vue'
import Week from './components/fresh-tip/Week.vue'

const envMode = import.meta.env.VITE_ENV_MODE

const RoguelikeFight = defineAsyncComponent(() => import('./components/RoguelikeFight.vue'))

const commonStore = useCommonStore()
const userStore = useUserStore()
const roomStore = useRoomStore()

const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}dungeon-tab`
const curTab = ref(localStorage.getItem(localKey) || 'common')
function handleTabChange(tab: string | number) {
  curTab.value = String(tab)
  localStorage.setItem(localKey, String(tab))
}

const challengeList: { name: string; key: DailyChallengeModeType; class: string }[] = [
  { name: '装备', key: 'equip', class: 'legend' },
  { name: '金币', key: 'gold', class: 'gold' },
  { name: '钻石', key: 'diamond', class: 'diamond' },
]
const dailyChallengeCollapsedKey = `war-soul-${envMode === 'test' ? 'test-' : ''}daily-challenge-collapsed`
const dailyChallengeCollapsed = ref(localStorage.getItem(dailyChallengeCollapsedKey) === '1')
function toggleDailyChallengeCollapsed() {
  dailyChallengeCollapsed.value = !dailyChallengeCollapsed.value
  localStorage.setItem(dailyChallengeCollapsedKey, dailyChallengeCollapsed.value ? '1' : '0')
}
const hasDailyChallengeTimes = computed(() => {
  const times = userStore.dailyChallengeTimes
  return times.equip > 0 || times.gold > 0 || times.diamond > 0
})
const maxChallengeLevel = computed(() => {
  return (type: DailyChallengeModeType) => userStore.dailyChallengeLevel[type]
})
const userCurMaxChallengeLevel = computed(() => (userStore.soulType > 3 ? 40 : userStore.advanceLevel === 4 ? 30 : 20))

const challengeLevel = reactive({
  equip: 1,
  gold: 1,
  diamond: 1,
})

const personMonster = ref<PersonMonsterType>()
const personFightType = ref<PersonFightModeType>()
const teamMonster = ref<NestMonsterType>()

const monsterCurHp = ref(0)
const useTime = ref(0)
async function handleStartDailyChallenge(type: DailyChallengeModeType) {
  if (userStore.dailyChallengeTimes[type] === 0) return ElMessage.warning('今日挑战次数已用尽')
  const res = await socketEmitAsync<PersonMonsterType>({ type: 'dailyChallenge', data: { type, level: challengeLevel[type] } })
  personFightType.value = type
  personMonster.value = res
  monsterCurHp.value = res.hp
}

function handleSweep() {
  const levels = userStore.dailyChallengeLevel
  if (userStore.vipLevel === 0 && (levels.equip < 3 || levels.gold < 3 || levels.diamond < 1))
    return ElMessageBox.alert(
      `<p class="affix">一键扫荡需通过<span class="legend">装备挑战</span> 3级；<span class="gold">金币挑战</span> 3级；<span class="diamond">钻石挑战</span> 1级，才可使用</p>`,
      '提示',
      {
        dangerouslyUseHTMLString: true,
      },
    )
  const times = userStore.dailyChallengeTimes
  if (!times.diamond && !times.equip && !times.gold) return ElMessage.warning('今日已无挑战次数')
  ElMessageBox.confirm('确认以当前最高通过难度等级清空每日挑战的剩余次数吗？', '提示', {
    type: 'warning',
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<{ dailyChallengeCount: Record<DailyChallengeModeType, number> }>({ type: 'sweepDailyChallenge' })
      commonStore.getMailList()
      userStore.dailyChallengeTimes.equip = 0
      userStore.dailyChallengeTimes.gold = 0
      userStore.dailyChallengeTimes.diamond = 0
      userStore.dailyChallengeCount = res.dailyChallengeCount
    })
    .catch(() => {})
}

function handleFightOver(res: 'fail' | 'success', creatorId?: number) {
  if (personMonster.value) {
    if (res === 'success') {
      if (['gold', 'diamond', 'equip'].includes(personMonster.value.type)) {
        const type = personMonster.value!.type as DailyChallengeModeType
        const level = personMonster.value!.level
        if (userStore.dailyChallengeLevel[type] < 40 && level > userStore.dailyChallengeLevel[type]) {
          userStore.dailyChallengeLevel[type]++
          challengeLevel[type]++
        }
      } else if (personMonster.value.type === 'eternalTower') {
        getEternalTowerInfo()
        getPersonFightInfo()
      } else if (personMonster.value.type === 'runeTower') {
        getRuneTowerInfo()
        getPersonFightInfo()
      } else if (personMonster.value.type === 'guide') {
        getGuideFightInfo()
      } else if (personMonster.value.type === 'endlessTower') {
        getEndlessTowerInfo()
        getPersonFightInfo()
      } else if (personMonster.value.type === 'boundlessTower') {
        getBoundlessTowerInfo()
        getPersonFightInfo()
      } else if (personMonster.value.type === 'secretCave') {
        userStore.consumables.secretCaveFight--
        getFightCount()
      }
    }
    personMonster.value = undefined
  } else if (teamMonster.value) {
    if (res === 'success') {
      if (teamMonster.value?.type === 'nest') {
        if (userStore.nestTimes > 0) {
          userStore.nestTimes--
          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          nestMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'plain') {
        if (userStore.plainTimes > 0) {
          userStore.plainTimes--
          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          plainMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'runeRuin') {
        if (userStore.runeRuinTimes > 0) {
          userStore.runeRuinTimes--
          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          runeRuinMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'runeTemple') {
        if (userStore.runeTempleTimes > 0) {
          userStore.runeTempleTimes--
          commonStore.getMailList()
        }
      } else if (teamMonster.value?.type === 'crack') {
        if (userStore.crackTimes > 0 || userStore.consumables.crackFight > 0) {
          if (userStore.crackTimes > 0) userStore.crackTimes--
          else userStore.consumables.crackFight--

          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          crackMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'wild') {
        commonStore.getMailList()
        useWildStore().getWildInfo()
      } else if (teamMonster.value?.type === 'advance') {
        getAdvanceMonster()
      } else if (teamMonster.value?.type === 'goldTemple') {
        if (userStore.goldTempleRewardTimes > 0) {
          userStore.goldTempleRewardTimes--
          commonStore.getMailList()
        }
        userStore.goldTempleTimes--
      } else if (teamMonster.value?.type === 'relicRuin') {
        if (userStore.relicRuinTimes > 0) {
          userStore.relicRuinTimes--
          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          relicRuinMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'skillCorridor') {
        if (userStore.skillCorridorTimes > 0) {
          userStore.skillCorridorTimes--
          commonStore.getMailList()
        }
        if (userStore.id === creatorId) {
          skillCorridorMonster.value = undefined
        }
      } else if (teamMonster.value?.type === 'drageonMountain') {
        if (userStore.drageonMountainRewardTimes > 0) {
          userStore.drageonMountainRewardTimes--
          commonStore.getMailList()
        }
      } else if (teamMonster.value?.type === 'peak') {
        getPeakMonster()
      } else if (teamMonster.value?.type === 'nian') {
        getNianMonsterInfo()
        commonStore.getMailList()
      }
      roomStore.clearRoomInfo()
    } else {
      if (teamMonster.value?.type === 'wild') {
        useWildStore().getWildInfo()
      }
    }
    teamMonster.value = undefined
  }
}

const eternalMonster = ref<PersonMonsterType>()
function getEternalTowerInfo() {
  socketEmit<EternalTowerInfoRes>({
    type: 'getEternalTowerInfo',
    data: { level: userStore.eternalTowerLevel },
    callBack: (res) => {
      eternalMonster.value = {
        ...res.data.monster,
        type: 'eternalTower',
      }
      userStore.eternalTowerTimes = res.data.times
      userStore.eternalTowerLevel = res.data.level
    },
  })
}
const nestMonster = ref<NestMonsterType>()
function getNestInfo() {
  socketEmit<NestInfoRes>({
    type: 'getNestInfo',
    callBack: (res) => {
      const monster = res.data.monster
      if (monster) {
        nestMonster.value = {
          ...monster,
          type: 'nest',
        }
      }
      userStore.nestTimes = res.data.times
    },
  })
}
const plainMonster = ref<NestMonsterType>()
function getPlainInfo() {
  if (userStore.soulType <= 3) return
  socketEmit<NestInfoRes>({
    type: 'getPlainInfo',
    callBack: (res) => {
      const monster = res.data.monster
      if (monster) {
        plainMonster.value = {
          ...monster,
          type: 'plain',
        }
      }
      userStore.plainTimes = res.data.times
    },
  })
}
const runeRuinMonster = ref<NestMonsterType>()
function getRuneRuinInfo() {
  if (!userStore.soulType) return
  socketEmit<NestInfoRes>({
    type: 'getRuneRuinInfo',
    callBack: (res) => {
      const monster = res.data.monster
      if (monster) {
        runeRuinMonster.value = {
          ...monster,
          type: 'runeRuin',
        }
      }
      userStore.runeRuinTimes = res.data.times
    },
  })
}
const runeMonster = ref<PersonMonsterType>()
function getRuneTowerInfo() {
  if (!userStore.soulType) return
  socketEmit<EternalTowerInfoRes>({
    type: 'getRuneTowerInfo',
    callBack: (res) => {
      if (!res.data) return
      const monster = res.data.monster
      if (monster) {
        runeMonster.value = {
          ...monster,
          type: 'runeTower',
        }
      }
      userStore.runeTowerTimes = res.data.times
      userStore.runeTowerLevel = res.data.level
    },
  })
}
const guideFightMonster = ref<PersonMonsterType>()
function getGuideFightInfo() {
  socketEmit<{ monster: PersonMonsterType; index: number }>({
    type: 'getGuideFightInfo',
    callBack: (res) => {
      guideFightMonster.value = {
        ...res.data.monster,
        type: 'guide',
      }
      userStore.guideFightIndex = res.data.index
    },
  })
}
const endlessMonster = ref<PersonMonsterType>()
function getEndlessTowerInfo() {
  if (userStore.advanceLevel !== 4) return
  socketEmit<EternalTowerInfoRes>({
    type: 'getEndlessTowerInfo',
    callBack: (res) => {
      endlessMonster.value = {
        ...res.data.monster,
        type: 'endlessTower',
      }
      userStore.endlessTowerTimes = res.data.times
      userStore.endlessTowerLevel = res.data.level
    },
  })
}
const runeTempleMonster = ref<NestMonsterType>()
function getRuneTempleInfo() {
  if (!userStore.soulType) return
  socketEmit<NestInfoRes>({
    type: 'getRuneTempleInfo',
    callBack: (res) => {
      runeTempleMonster.value = {
        ...res.data.monster,
        type: 'runeTemple',
      }
      userStore.runeTempleTimes = res.data.times
    },
  })
}
const crackMonster = ref<NestMonsterType>()
function getCrackInfo() {
  if (!userStore.soulType) return
  socketEmit<NestInfoRes>({
    type: 'getCrackInfo',
    callBack: (res) => {
      crackMonster.value = {
        ...res.data.monster,
        type: 'crack',
      }
      userStore.crackTimes = res.data.times
    },
  })
}
const advanceMonster = ref<NestMonsterType>()
function getAdvanceMonster() {
  if (userStore.runeTempleTimes !== 0 || userStore.advanceLevel === 4) return
  socketEmit<{ monster: NestMonsterType; times: number; level: number }>({
    type: 'getAdvanceRoadFightInfo',
    callBack: (res) => {
      if (!res.data) return
      advanceMonster.value = {
        ...res.data.monster,
        type: 'advance',
      }
      userStore.advanceTimes = res.data.times
      userStore.advanceLevel = res.data.level
    },
  })
}
const goldTempleMonster = ref<NestMonsterType>()
const goldTempleCurRank = ref(0)
const goldTempleMonsterHpMag = ref(1)
function getGoldTempleInfo() {
  if (userStore.advanceLevel !== 4) return
  socketEmit<GoldTempleInfoRes>({
    type: 'getGoldTempleInfo',
    callBack: (res) => {
      goldTempleMonster.value = {
        ...res.data.monster,
        type: 'goldTemple',
      }
      userStore.goldTempleTimes = res.data.times
      userStore.goldTempleRewardTimes = res.data.rewardTimes
    },
  })
}
const relicRuinMonster = ref<NestMonsterType>()
function getRelicRuinInfo() {
  if (userStore.advanceLevel !== 4) return
  socketEmit<NestInfoRes>({
    type: 'getRelicRuinInfo',
    callBack: (res) => {
      relicRuinMonster.value = {
        ...res.data.monster,
        type: 'relicRuin',
      }
      userStore.relicRuinTimes = res.data.times
    },
  })
}
const skillCorridorMonster = ref<NestMonsterType>()
function getSkillCorridorInfo() {
  if (userStore.soulType <= 3) return
  socketEmit<NestInfoRes>({
    type: 'getSkillCorridorInfo',
    callBack: (res) => {
      skillCorridorMonster.value = {
        ...res.data.monster,
        type: 'skillCorridor',
      }
      userStore.skillCorridorTimes = res.data.times
    },
  })
}

const drageonMountainMonster = ref<NestMonsterType>()
const drageonMountainCurRank = ref(0)
const drageonMountainMonsterHpMag = ref(1)
function getDrageonMountainInfo() {
  if (userStore.advanceLevel !== 4) return
  socketEmit<GoldTempleInfoRes>({
    type: 'getDrageonMountainInfo',
    callBack: (res) => {
      drageonMountainMonster.value = {
        ...res.data.monster,
        type: 'drageonMountain',
      }
      userStore.drageonMountainTimes = res.data.times
      userStore.drageonMountainRewardTimes = res.data.rewardTimes
    },
  })
}
function handleDrageonMountainRankChange(rank: number) {
  drageonMountainMonsterHpMag.value = drageonMountainRankHpMag[rank]
  drageonMountainCurRank.value = rank
}

const peakMonster = ref<NestMonsterType>()
function getPeakMonster() {
  if (userStore.goldTempleRewardTimes !== 0 || userStore.endlessTowerLevel < 49 || userStore.peakLevel === 8) return
  socketEmit<{ monster: NestMonsterType; times: number; level: number }>({
    type: 'getPeakRoadFightInfo',
    callBack: (res) => {
      if (res.data.monster) {
        peakMonster.value = {
          ...res.data.monster,
          type: 'peak',
        }
      }
      userStore.peakTimes = res.data.times
      userStore.peakLevel = res.data.level
    },
  })
}

const boundlessMonster = ref<PersonMonsterType>()
function getBoundlessTowerInfo() {
  if (userStore.soulType <= 3) return
  socketEmit<EternalTowerInfoRes>({
    type: 'getBoundlessTowerInfo',
    callBack: (res) => {
      boundlessMonster.value = {
        ...res.data.monster,
        type: 'boundlessTower',
      }
      userStore.boundlessTowerTimes = res.data.times
      userStore.boundlessTowerLevel = res.data.level
    },
  })
}

async function getPersonFightInfo() {
  const res = await socketEmitAsync<PersonFightInfoType | undefined>({ type: 'getPersonFightInfo' })
  if (res?.monster) {
    personMonster.value = { ...res.monster, type: res.type }
    personFightType.value = res.type
    monsterCurHp.value = res.monsterCurHp || 0
    useTime.value = res.useTime
    return true
  }
  return false
}
async function getTeamFightInfo() {
  const res = await socketEmitAsync<TeamFightInfoType | undefined>({ type: 'getTeamFightInfo' })
  if (res?.monster) {
    teamMonster.value = { ...res.monster, type: res.type }
    monsterCurHp.value = res.monsterCurHp || 0
    teamMonster.value.type = res.type
    useTime.value = res.useTime
    return true
  }
  return false
}
function handleStartEternalTower() {
  personMonster.value = eternalMonster.value
  useTime.value = 0
}
function handleStartRuneTower() {
  personMonster.value = runeMonster.value
  useTime.value = 0
}
function handleStartGuide() {
  personMonster.value = guideFightMonster.value
  useTime.value = 0
}
function handleStartNest(monster: NestMonsterType) {
  if (monster.type === 'drageonMountain') userStore.drageonMountainTimes--
  if (monster.type === 'nian') nianMonsterFightCount.value--
  teamMonster.value = { ...monster }
  useTime.value = 0
}
function handleStartRuneTemple() {
  teamMonster.value = runeTempleMonster.value
  useTime.value = 0
}
function handleStartWild(monster: NestMonsterType) {
  teamMonster.value = { ...monster, type: 'wild' }
  useTime.value = 0
}
function handlestartAdvanceRoad() {
  teamMonster.value = advanceMonster.value
  useTime.value = 0
}
function handleStartEndlessTower() {
  personMonster.value = endlessMonster.value
  useTime.value = 0
}
function handleStartGoldTemple() {
  if (!goldTempleMonster.value) return
  const realHp = Math.round(goldTempleMonster.value.hp * goldTempleMonsterHpMag.value)
  teamMonster.value = {
    ...goldTempleMonster.value,
    hp: Math.round(goldTempleMonster.value.hp * goldTempleMonsterHpMag.value),
    special: [...goldTempleMonster.value.special!.slice(0, goldTempleCurRank.value === 4 ? undefined : 3)],
  }
  monsterCurHp.value = realHp
  useTime.value = 0
}
function handleGoldTempleRankChange(rank: number) {
  goldTempleMonsterHpMag.value = goldTempleRank[rank].hpMag
  goldTempleCurRank.value = rank
}
function handleStartPeakRoad() {
  teamMonster.value = peakMonster.value
  useTime.value = 0
}
function handleStartBoundlessTower() {
  personMonster.value = boundlessMonster.value
  useTime.value = 0
}
function handleStartSecetCave() {
  personMonster.value = {
    name: '紫晶宝箱',
    hp: 160,
    def: 0,
    evs: 0,
    crtdef: 0,
    special: [{ name: '巨硬', key: 'superHard', data: {} }],
    type: 'secretCave',
    level: 0,
  }
  useTime.value = 0
}

const TeamFightRoomRef = ref()
const teamFightRoomShow = ref(false)
function handleShowRoom(type: RoomType | '') {
  TeamFightRoomRef.value.curTab = type
  teamFightRoomShow.value = true
}
function handleStartTeamFight(monster: NestMonsterType) {
  teamMonster.value = { ...monster }
  useTime.value = 0
  teamFightRoomShow.value = false
}

const fightCount = ref(0)
function getFightCount() {
  socketEmit<number>({
    type: 'getSecretCaveFightCount',
    callBack: (res) => {
      fightCount.value = res.data
    },
  })
}

const nianMonster = ref<NianMonsterType>()
const nianMonsterFightCount = ref(0)
function getNianMonsterInfo() {
  socketEmit<{ monster: NianMonsterType; fightCount: number }>({
    type: 'getNianMonsterInfo',
    callBack: (res) => {
      nianMonster.value = res.data.monster
      nianMonsterFightCount.value = res.data.fightCount
    },
  })
}
function handleStartNianMonster() {
  teamMonster.value = {
    ...nianMonster.value,
    type: 'nian',
  } as any
  useTime.value = 0
}

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return

    if (userStore.dailyChallengeLevel) {
      const { equip, gold, diamond } = userStore.dailyChallengeLevel
      challengeLevel.equip = equip + 1
      challengeLevel.gold = gold + 1
      challengeLevel.diamond = diamond + 1
    }
    userStore.setDailyChallengeInfo()
    getEternalTowerInfo()
    getRuneTowerInfo()
    getEndlessTowerInfo()
    getNestInfo()
    if (userStore.soulType && userStore.soulType <= 3) useWildStore().getWildInfo()
    getPlainInfo()
    getRuneRuinInfo()
    getCrackInfo()
    getRelicRuinInfo()
    getSkillCorridorInfo()
    getDrageonMountainInfo()
    getBoundlessTowerInfo()
    getPersonFightInfo()
    getTeamFightInfo()
    if (userStore.guideFightIndex < 10) getGuideFightInfo()
    getAdvanceMonster()
    getPeakMonster()

    if (dayjs().isBefore(dayjs('2026-03-08'))) {
      getFightCount()
      getNianMonsterInfo()
      socketOn({
        type: 'nianMonsterHpChange',
        callBack: (res: number) => {
          nianMonster.value && (nianMonster.value.curHp = res)
        },
      })
    }
  },
)
watch(
  () => commonStore.isSocketReconnect,
  async (val) => {
    if (!val) return
    userStore.setDailyChallengeInfo()
    getPersonFightInfo().then((res) => {
      if (!res && personMonster.value) {
        const type = personMonster.value.type
        if (type === 'eternalTower') {
          personMonster.value = undefined
          getEternalTowerInfo()
        } else if (type === 'runeTower') {
          personMonster.value = undefined
          getRuneTowerInfo()
        } else if (type === 'guide') {
          personMonster.value = undefined
          getGuideFightInfo()
        } else if (type === 'endlessTower') {
          personMonster.value = undefined
          getEndlessTowerInfo()
        } else if (type === 'boundlessTower') {
          personMonster.value = undefined
          getBoundlessTowerInfo()
        } else {
          const type = personMonster.value!.type as DailyChallengeModeType
          const level = personMonster.value!.level
          if (userStore.dailyChallengeLevel[type] < 30 && level > userStore.dailyChallengeLevel[type]) {
            userStore.dailyChallengeLevel[type]++
            challengeLevel[type]++
          }
          personMonster.value = undefined
        }
      }
    })
    getTeamFightInfo().then((res) => {
      if (teamMonster.value?.type === 'advance') {
        getAdvanceMonster()
        if (userStore.advanceLevel >= 3) commonStore.getMailList()
      } else if (!res && teamMonster.value) {
        teamMonster.value = undefined
        getNestInfo()
        getPlainInfo()
        getRuneRuinInfo()
        getCrackInfo()
        getRelicRuinInfo()
        getSkillCorridorInfo()
        getDrageonMountainInfo()
        getPeakMonster()
      }
    })
    getEternalTowerInfo()
    getRuneTowerInfo()
    getEndlessTowerInfo()
    getBoundlessTowerInfo()
  },
)

watch(
  () => userStore.soulType,
  (_val, oldVal) => {
    if (oldVal !== 0) return

    getRuneTowerInfo()
    getRuneRuinInfo()
    getRuneTempleInfo()
    getCrackInfo()
    getEndlessTowerInfo()
    getGoldTempleInfo()
    getRelicRuinInfo()
    getSkillCorridorInfo()
    getDrageonMountainInfo()
    getBoundlessTowerInfo()
  },
)

watch(
  () => userStore.runeTempleTimes,
  (val) => {
    if (val === 0) return

    getAdvanceMonster()
  },
)
watch(
  () => userStore.goldTempleTimes,
  (val) => {
    if (val === 0) return

    getDrageonMountainInfo()
  },
)

const roguelikeGameStore = useRoguelikeGameStore()
</script>

<template>
  <div class="dungeon-page page">
    <div class="dungeon-list" v-show="!personMonster && !teamMonster && !roguelikeGameStore.gameState.started">
      <div class="dungeon affix" style="position: relative">
        <div class="title">
          <h2 class="unique">每日挑战</h2>
          <el-button type="primary" plain style="height: 25px; margin-left: 10px" @click="handleSweep" :disabled="!hasDailyChallengeTimes"
            >一键扫荡</el-button
          >
          <div class="help-info" style="margin-left: 5px">
            <el-popover placement="top" width="100%" trigger="click">
              <template #reference>
                <el-icon size="20">
                  <QuestionFilled />
                </el-icon>
              </template>
              <div class="affix">
                <h4>每日挑战说明</h4>
                <p>1. 每日挑战<span class="info">每日0点刷新</span>。</p>
                <p>2. 挑战失败时，不消耗次数。</p>
                <p>
                  3. 保底/额外奖励说明：当次数归零时，<span class="legend">装备挑战</span>必出<span class="legend">传说</span
                  >品质装备（若中途获得，则重置保底）；<span class="gold">金币挑战</span>中，额外奖励为 <span class="gold">1,000,000金币</span>；<span
                    class="diamond"
                    >钻石挑战</span
                  >中，额外奖励为 <span class="diamond">300钻石</span>。
                </p>
                <p>
                  4. 当挑战等级提升时：<span class="legend">装备挑战</span>提升装备的品质爆率；<span class="gold">金币挑战</span>提升金币产出；<span
                    class="diamond"
                    >钻石挑战</span
                  >提升钻石产出。
                </p>
                <p>5. 每日挑战及每周挑战中的战斗均<span class="grow">支持离线</span>进行。</p>
                <template v-if="userStore.advanceLevel === 4">
                  <p>
                    6. 20层以上的<span class="legend">装备挑战</span>将概率产出<span class="darkGold">暗金</span>装备，同时保底也为<span
                      class="darkGold"
                      >暗金</span
                    >装备。
                  </p>
                </template>
                <template v-if="userStore.soulType > 3">
                  <p>
                    6. 30层以上的<span class="legend">装备挑战</span>将概率产出<span class="supreme">至臻</span>装备，同时保底也为<span
                      class="supreme"
                      >至臻</span
                    >装备。
                  </p>
                </template>
              </div>
            </el-popover>
          </div>
          <el-button type="primary" link plain style="position: absolute; right: 10px" @click="toggleDailyChallengeCollapsed">
            {{ dailyChallengeCollapsed ? '展开' : '收起' }}
            <el-icon>
              <ArrowDown v-show="dailyChallengeCollapsed" />
              <ArrowUp v-show="!dailyChallengeCollapsed" />
            </el-icon>
          </el-button>
        </div>
        <div class="daily-challenge-summary" v-show="dailyChallengeCollapsed">
          <table border style="width: 100%; text-align: center">
            <tr>
              <td>挑战</td>
              <td><span class="legend">装备挑战</span></td>
              <td><span class="gold">金币挑战</span></td>
              <td><span class="diamond">钻石挑战</span></td>
            </tr>
            <tr>
              <td>剩余次数</td>
              <td>{{ userStore.dailyChallengeTimes.equip }}</td>
              <td>{{ userStore.dailyChallengeTimes.gold }}</td>
              <td>{{ userStore.dailyChallengeTimes.diamond }}</td>
            </tr>
          </table>
        </div>
        <div v-show="!dailyChallengeCollapsed">
          <div class="border-wrap daily-challenge" v-for="item of challengeList" :key="item.key">
            <div class="left" style="width: 100%">
              <h4 :class="item.class">{{ item.name }}挑战</h4>
              <p>
                <span style="margin-right: 15px">次数：{{ userStore.dailyChallengeTimes[item.key] }}</span>
                <span style="margin-right: 15px">
                  已通过：<span :class="maxChallengeLevel(item.key) === userCurMaxChallengeLevel ? 'grow' : ''"
                    >{{ maxChallengeLevel(item.key) }} / {{ userCurMaxChallengeLevel }}</span
                  >
                </span>
                <span>{{ item.key === 'equip' ? '保底' : '额外奖励' }}：{{ 10 - userStore.dailyChallengeCount[item.key] }}</span>
              </p>
              <div class="difficulty" v-show="userStore.dailyChallengeLevel[item.key] < userCurMaxChallengeLevel">
                <span>挑战等级：</span>
                <el-input-number
                  size="small"
                  v-model="challengeLevel[item.key]"
                  :min="1"
                  :max="maxChallengeLevel(item.key) + 1 > userCurMaxChallengeLevel ? maxChallengeLevel(item.key) : maxChallengeLevel(item.key) + 1"
                  style="width: 90px"
                ></el-input-number>
                <el-button size="small" type="success" plain @click="handleStartDailyChallenge(item.key)" style="margin-left: 15px; width: 90px">
                  开始
                </el-button>
              </div>
            </div>
            <div class="challenge-info"></div>
          </div>
        </div>
      </div>

      <div
        class="dungeon affix"
        v-if="
          userStore.guideFightIndex < 10 ||
          (userStore.soulType && userStore.runeTempleTimes === 0 && userStore.advanceLevel !== 4) ||
          (userStore.advanceLevel === 4 && userStore.goldTempleRewardTimes === 0 && userStore.peakLevel !== 8) ||
          dayjs().isBefore('2026-03-08 00:00:00')
        "
      >
        <div class="title">
          <h2 class="unique" style="margin-right: 10px">特别挑战</h2>
        </div>
        <SecretCave @start="handleStartSecetCave" :fight-count="fightCount" v-if="dayjs().isBefore('2026-03-08 00:00:00')" />
        <NianMonster
          @start="handleStartNianMonster"
          :monster="nianMonster"
          :fight-count="nianMonsterFightCount"
          v-if="dayjs().isBefore('2026-03-08 00:00:00')"
        />
        <Guide :monster="guideFightMonster" @start="handleStartGuide" v-if="userStore.guideFightIndex < 10" />
      </div>

      <el-tabs v-model="curTab" stretch @tab-change="handleTabChange">
        <div style="display: flex; justify-content: center">
          <el-button type="primary" size="small" plain @click="handleShowRoom('')" style="width: 90%; margin-bottom: 10px">组队房间</el-button>
        </div>

        <el-tab-pane label="普通" name="common" class="pane" lazy>
          <div class="dungeon affix">
            <Week />
            <EternalTower
              :eternal-monster="eternalMonster"
              @start="handleStartEternalTower"
              @sweep="getEternalTowerInfo"
              v-show="userStore.soulType <= 3"
            />
            <Nest
              :nest-monster="nestMonster"
              @get-monster="(res) => (nestMonster = res)"
              @start="handleStartNest"
              @give-up="nestMonster = undefined"
              @sweep="getNestInfo"
              @show-room="handleShowRoom('nest')"
              v-show="userStore.nestTimes || nestMonster || userStore.dailyChallengeLevel.diamond < 3 || userStore.soulType <= 3"
            />
            <el-divider v-if="!userStore.soulType">
              <el-popover placement="bottom" width="100%" trigger="click">
                <template #reference>
                  <a href="javascript:;">觉醒</a>
                </template>
                <p class="affix" style="text-align: center">
                  通过<span class="antiquity">不朽之塔</span> <span class="grow">30</span>层后，可前往<span class="info">主城</span>的觉醒殿堂进行觉醒
                </p>
              </el-popover>
            </el-divider>
            <RuneTower :monster="runeMonster" @start="handleStartRuneTower" @sweep="getRuneTowerInfo" />
            <RuneRuin
              :runeRuinMonster="runeRuinMonster"
              @get-monster="(res) => (runeRuinMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('runeRuin')"
              v-show="userStore.runeRuinTimes || runeRuinMonster || !userStore.soulType"
            />
            <RuneTemple :monster="runeTempleMonster" @start="handleStartRuneTemple" @sweep="getRuneTowerInfo" />
            <Crack
              :crackMonster="crackMonster"
              @get-monster="(res) => (crackMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('crack')"
              v-show="userStore.crackTimes || crackMonster || !userStore.soulType || userStore.consumables.crackFight"
            />
          </div>
        </el-tab-pane>
        <el-tab-pane label="进阶" name="advance" class="pane" lazy>
          <div class="dungeon affix">
            <template v-if="userStore.advanceLevel !== 4">
              <div class="title">
                <h2 class="unique" style="margin-right: 10px">特别挑战</h2>
              </div>
              <AdvanceRoad :monster="advanceMonster" @start="handlestartAdvanceRoad" />
            </template>

            <Week />
            <EndlessTower :monster="endlessMonster" @start="handleStartEndlessTower" @sweep="getEndlessTowerInfo" />
            <RelicRuin
              :monster="relicRuinMonster"
              @get-monster="(res) => (relicRuinMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('relicRuin')"
              v-show="userStore.relicRuinTimes || relicRuinMonster || userStore.advanceLevel !== 4"
            />
            <GoldTemple
              :monster="
                goldTempleMonster
                  ? {
                      ...goldTempleMonster,
                      hp: Math.round(goldTempleMonster.hp * goldTempleMonsterHpMag),
                      special: [...goldTempleMonster.special!.slice(0, goldTempleCurRank === 4 ? undefined : 3)],
                    }
                  : undefined
              "
              @start="handleStartGoldTemple"
              @rankChange="handleGoldTempleRankChange"
            />
            <DrageonMountain
              :monster="
                drageonMountainMonster
                  ? {
                      ...drageonMountainMonster,
                      hp: Math.round(drageonMountainMonster.hp * drageonMountainMonsterHpMag),
                      rank: drageonMountainCurRank,
                    }
                  : undefined
              "
              @get-monster="(res) => (drageonMountainMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('drageonMountain')"
              @rankChange="handleDrageonMountainRankChange"
            />
          </div>
        </el-tab-pane>
        <el-tab-pane label="巅峰" name="peak" class="pane" lazy v-if="envMode !== 'prod'">
          <div class="dungeon affix">
            <template v-if="userStore.soulType <= 3">
              <div class="title">
                <h2 class="unique" style="margin-right: 10px">特别挑战</h2>
              </div>
              <PeakRoad :monster="peakMonster" @start="handleStartPeakRoad" />
            </template>
            <Week />
            <BoundlessTower :monster="boundlessMonster" @start="handleStartBoundlessTower" @sweep="getBoundlessTowerInfo" />
            <Plain
              :plain-monster="plainMonster"
              @get-monster="(res) => (plainMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('plain')"
            />
            <SkillCorridor
              :monster="skillCorridorMonster"
              @get-monster="(res) => (skillCorridorMonster = res)"
              @start="handleStartNest"
              @show-room="handleShowRoom('skillCorridor')"
              v-show="userStore.skillCorridorTimes || skillCorridorMonster"
            />
          </div>
        </el-tab-pane>
      </el-tabs>

      <div class="dungeon affix" v-if="userStore.soulType <= 3">
        <div class="title">
          <h2 class="unique" style="margin-right: 10px">每月挑战</h2>
          <el-countdown
            format="D天 HH时 mm分 ss秒 后重置"
            :value="dayjs().add(1, 'month').startOf('month')"
            value-style="font-size: 12px"
          ></el-countdown>
        </div>
        <Wild @start="handleStartWild" />
      </div>
      <div class="dungeon affix">
        <div class="title">
          <h2 class="unique">轮换活动</h2>
        </div>
        <RoguelikePanel />
      </div>
    </div>

    <TeamFightRoom
      ref="TeamFightRoomRef"
      v-show="teamFightRoomShow"
      :room-show="teamFightRoomShow"
      :nest-monster="nestMonster"
      :plain-monster="plainMonster"
      :rune-ruin-monster="runeRuinMonster"
      :crack-monster="crackMonster"
      :relic-ruin-monster="relicRuinMonster"
      :skill-corridor-monster="skillCorridorMonster"
      :drageon-mountain-monster="
        drageonMountainMonster
          ? { ...drageonMountainMonster, hp: Math.round(drageonMountainMonster.hp * drageonMountainMonsterHpMag), rank: drageonMountainCurRank }
          : undefined
      "
      @back="teamFightRoomShow = false"
      @start="handleStartTeamFight"
    />
    <DungeonFight :monster="personMonster" :monsterCurHp="monsterCurHp" :useTime="useTime" v-show="personMonster" @fightOver="handleFightOver" />
    <TeamFight
      :monster="teamMonster"
      :monsterCurHp="monsterCurHp"
      :useTime="useTime"
      v-show="teamMonster"
      @fightOver="handleFightOver"
      @getTeamFightInfo="getTeamFightInfo"
    />
    <RoguelikeFight v-if="roguelikeGameStore.gameState.started"></RoguelikeFight>

    <FooterBar />
  </div>
</template>

<style lang="scss">
.dungeon-page {
  height: 100%;
  position: relative;

  .title {
    display: flex;
    align-items: center;
    margin: 0 0 10px 10px;

    h2 {
      text-align: left;
      font-size: 22px;
      margin: 0;
    }
  }

  .dungeon-list {
    height: 100%;
    overflow: auto;

    .dungeon {
      font-size: 14px;
    }
  }

  .border-wrap {
    margin-bottom: 10px;
    padding: 5px 10px;

    .left {
      flex-shrink: 0;
      text-align: left;
    }

    h4 {
      display: flex;
      align-items: center;
      text-align: left;
      font-size: 20px;
      margin: 10px 0;
    }

    p {
      margin: 4px 0;
    }

    .difficulty {
      display: flex;
      align-items: center;
    }

    .help-info {
      color: var(--fontColor);
      margin-left: 10px;
      display: flex;
      justify-content: center;

      p {
        margin: 8px 0;
      }
    }
  }

  .daily-challenge {
    display: flex;
  }

  .daily-challenge-summary {
    margin-bottom: 10px;
  }
}
</style>
